// src/components/Navbar.jsx
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const location = useLocation();
  const navigate = useNavigate();
  const navItems = ["home", "about", "facilities", "events", "gallery", "contact"];

  // Handle scroll for sticky navbar and active section
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      if (location.pathname === "/") {
        let current = "home";
        navItems.forEach((id) => {
          const section = document.getElementById(id);
          if (section) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= 120 && rect.bottom >= 120) current = id;
          }
        });
        setActiveSection(current);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loginOpen && !e.target.closest(".login-dropdown")) setLoginOpen(false);
      if (mobileMenuOpen && !e.target.closest("nav") && !e.target.closest(".mobile-menu")) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loginOpen, mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "unset";
  }, [mobileMenuOpen]);

  // Smooth scroll to section
  const handleNavClick = (section) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      const el = document.getElementById(section);
      if (el) {
        const offset = el.getBoundingClientRect().top + window.scrollY - 64;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    }
    setLoginOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-md shadow-sm"
        }`}
      >
        <nav className="w-full h-16 lg:h-18 flex justify-between items-center px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 max-w-[1920px] mx-auto">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => {
              setActiveSection("home");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="text-maroon font-extrabold text-lg sm:text-xl md:text-2xl tracking-wide hover:opacity-80 transition-opacity flex-shrink-0"
          >
            BCWD Surathkal
          </Link>

          {/* Desktop Menu */}
          <ul className="hidden md:flex gap-3 lg:gap-5 xl:gap-7 text-sm lg:text-base font-medium text-gray-700 items-center">
            {navItems.map((item) => (
              <li key={item}>
                <button
                  onClick={() => handleNavClick(item)}
                  className={`relative capitalize bg-transparent border-none cursor-pointer transition-all duration-300 hover:text-maroon px-2 py-1 ${
                    activeSection === item
                      ? "text-maroon font-bold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-maroon after:rounded-full"
                      : "text-gray-700"
                  }`}
                >
                  {item}
                </button>
              </li>
            ))}

            {/* Login Dropdown */}
            <li className="relative login-dropdown ml-2">
              <button
                onClick={() => setLoginOpen((prev) => !prev)}
                className="bg-maroon text-white px-4 lg:px-5 py-2 lg:py-2.5 rounded-lg hover:bg-maroonDark transition-all duration-300 select-none text-sm lg:text-base whitespace-nowrap font-medium shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
              >
                Login ▼
              </button>
              {loginOpen && (
                <ul className="absolute right-0 mt-3 w-44 lg:w-48 bg-white rounded-lg shadow-xl text-gray-700 font-medium z-20 overflow-hidden border border-gray-100 animate-slideDown">
                  <li>
                    <Link
                      to="/student-login"
                      onClick={() => setLoginOpen(false)}
                      className="block px-4 py-3 hover:bg-maroon hover:text-white text-sm lg:text-base transition-all"
                    >
                      👨‍🎓 Student Login
                    </Link>
                  </li>
                  <li className="border-t border-gray-100">
                    <Link
                      to="/login"
                      onClick={() => setLoginOpen(false)}
                      className="block px-4 py-3 hover:bg-maroon hover:text-white text-sm lg:text-base transition-all"
                    >
                      👤 Warden Login
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="md:hidden text-maroon font-bold text-3xl focus:outline-none transition-transform duration-200 active:scale-90 z-50 w-10 h-10 flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? "✖" : "☰"}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 animate-fadeIn"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Slide-out Menu */}
      <div
        className={`mobile-menu md:hidden fixed top-16 right-0 bottom-0 w-72 sm:w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out overflow-y-auto ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-5 py-6 space-y-4">
          {/* Navigation Items */}
          <div className="space-y-2">
            {navItems.map((item) => (
              <button
                key={item}
                onClick={() => handleNavClick(item)}
                className={`block w-full text-left capitalize py-3 px-4 rounded-lg transition-all duration-200 font-medium ${
                  activeSection === item
                    ? "bg-maroon text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100 hover:text-maroon active:bg-gray-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {/* Login Options */}
          <div className="pt-4 border-t border-gray-200 space-y-2">
            <Link
              to="/student-login"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 py-3 px-4 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-maroon transition-all duration-200 font-medium active:bg-gray-200"
            >
              <span className="text-xl">👨‍🎓</span> Student Login
            </Link>
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 py-3 px-4 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-maroon transition-all duration-200 font-medium active:bg-gray-200"
            >
              <span className="text-xl">👤</span> Warden Login
            </Link>
          </div>

          {/* Footer */}
          <div className="pt-6 mt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              BCWD Surathkal Hostel <br />© {new Date().getFullYear()} All rights reserved
            </p>
          </div>
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slideDown { animation: slideDown 0.2s ease-out; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
}
